import { documentToHtmlString } from '@contentful/rich-text-html-renderer'
import type { Document } from '@contentful/rich-text-types'
import { fixImageUrlProtocol } from '@/utils/contentfulHelpers'

// Icon HTML generators
const getPawIconHtml = (color: string = '#43CC9E') => {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none" class="list-icon">
    <g clip-path="url(#clip0_3946_12204)">
      <path d="M11.1172 5.22166C11.7875 7.2326 11.1032 9.26229 9.5891 9.75916C8.07504 10.256 6.30316 9.02791 5.63285 7.01698C4.96254 5.00604 5.64691 2.97635 7.16098 2.47948C8.67504 1.9826 10.4469 3.21073 11.1172 5.22166ZM5.20629 10.1764C6.09223 11.6951 5.8766 13.4623 4.72816 14.1185C3.57973 14.7748 1.92973 14.0764 1.04848 12.5576C0.167226 11.0389 0.373476 9.27166 1.52191 8.61541C2.67035 7.95916 4.32035 8.6576 5.2016 10.1764H5.20629ZM3.74379 19.6732C6.20004 13.0498 10.5641 11.367 12.5 11.367C14.436 11.367 18.8 13.0498 21.2563 19.6732C21.425 20.1279 21.5 20.6154 21.5 21.1029V21.1779C21.5 22.3873 20.5203 23.367 19.311 23.367C18.7719 23.367 18.2375 23.3014 17.7172 23.1701L13.5922 22.1389C12.875 21.9607 12.125 21.9607 11.4079 22.1389L7.28285 23.1701C6.76254 23.3014 6.22816 23.367 5.6891 23.367C4.47973 23.367 3.50004 22.3873 3.50004 21.1779V21.1029C3.50004 20.6154 3.57504 20.1279 3.74379 19.6732ZM20.2719 14.1185C19.1235 13.4623 18.9079 11.6951 19.7938 10.1764C20.6797 8.6576 22.325 7.95916 23.4735 8.61541C24.6219 9.27166 24.8375 11.0389 23.9516 12.5576C23.0657 14.0764 21.4203 14.7748 20.2719 14.1185ZM15.036 9.75916C13.5219 9.26229 12.8375 7.2326 13.5079 5.22166C14.1782 3.21073 15.95 1.9826 17.4641 2.47948C18.9782 2.97635 19.6625 5.00604 18.9922 7.01698C18.3219 9.02791 16.55 10.256 15.036 9.75916Z" fill="${color}"/>
    </g>
    <defs>
      <clipPath id="clip0_3946_12204">
        <rect width="24" height="24" fill="white" transform="translate(0.5 0.867188)" />
      </clipPath>
    </defs>
  </svg>`
}

const getCheesecakeIconHtml = (color: string = '#43CC9E') => {
  return `<svg id="Capa_1" enable-background="new 0 0 511.81 511.81" height="512" viewBox="0 0 511.81 511.81" width="512" xmlns="http://www.w3.org/2000/svg" class="list-icon">
    <g>
      <path d="m16.008 478.516h439.25c31.233 0 56.552-25.319 56.552-56.552v-149.143c0-10.728-8.457-19.809-19.184-19.983-10.318-.167-18.853 7.724-19.739 17.778-.015 1.596-.057 2.74-.087 3.387v139.324c0 8.284-6.716 15-15 15h-457.8v34.181c0 8.841 7.167 16.008 16.008 16.008z" fill="${color}"/>
      <path d="m400.977 104.654c22.873 22.574 40.735 48.744 53.092 77.783 7.203 16.927 11.679 32.829 14.448 46.505 7.064-3.888 15.171-6.107 23.788-6.107 5.523 0 10.831.923 15.796 2.599-16.287-99.584-83.297-156.667-140.707-187.933-11.755-6.401-26.315-5.435-37.091 2.464l-11.706 8.551c32.066 14.948 59.706 33.76 82.38 56.138z" fill="${color}"/>
      <path d="m30.964 258.624h411.457c-1.118-14.505-4.812-38.741-16.516-65.741-10.921-25.191-26.626-47.916-46.68-67.543-23.602-23.099-53.407-42.027-88.675-56.337z" fill="${color}"/>
      <path d="m0 288.624h442.799v109.703h-442.799z" fill="${color}"/>
    </g>
  </svg>`
}

type ListVariant = 'default' | 'recipe'

export const useRichText = (document: Document, cfg?: { listVariant?: ListVariant }) => {
  const rendererOptions = {
    renderNode: {
      'embedded-asset-block': (node: any) => {
        const asset = node.data?.target
        if (!asset?.fields?.file?.url) return ''
        console.log('Rendering embedded asset with URL:', asset.fields.file.url)
        const imageUrl = fixImageUrlProtocol(asset.fields.file.url)

        const alt = asset.fields.description || asset.fields.title || ''
        const title = asset.fields.title || ''

        return `<img src="${imageUrl}" alt="${alt}" title="${title}" class="contentful-image" />`
      },
      'entry-hyperlink': (node: any, next: any) => {
        // Try Contentful Asset (legacy)
        let url = node.data?.target?.fields?.file?.url
        // Try Cloudinary (new)
        if (!url) {
          url =
            node.data?.target?.fields?.cloudinaryImage?.[0]?.secure_url ||
            node.data?.target?.fields?.cloudinaryImage?.[0]?.url
        }
        const content = next(node.content)
        if (url) {
          // Fix protocol if needed
          url = fixImageUrlProtocol(url)
          return `<a href="${url}" target="_blank" rel="noopener" class="rich-text-link">${content}</a>`
        }
        // fallback: just show the text
        return content
      },
      hyperlink: (node: any, next: any) => {
        const href = node.data?.uri || '#'
        const content = next(node.content)

        // Check if any text content in the link has bold marks
        const hasBoldContent = node.content?.some((textNode: any) =>
          textNode.marks?.some((mark: any) => mark.type === 'bold'),
        )

        if (hasBoldContent) {
          return `<strong><a href="${href}" class="rich-text-link">${content.replace(/<strong>|<\/strong>/g, '')} <span class="rich-text-link-icon"></span></a></strong>`
        }

        return `<a href="${href}" class="rich-text-link">${content}</a>`
      },
      'unordered-list': (node: any, next: any) => {
        const isRecipeMode = (cfg?.listVariant || 'default') === 'recipe'
        let anyRecipe = false
        const itemsHtml = (node.content || [])
          .map((li: any) => {
            const inner = next(li.content)
            const isRecipe = isRecipeMode || /ingrediens|recept|steg/i.test(inner)
            if (isRecipe) anyRecipe = true
            const icon = isRecipe ? getCheesecakeIconHtml() : getPawIconHtml()
            return `<li class="rich-text-list-item">${icon}${inner}</li>`
          })
          .join('')

        const listClass =
          isRecipeMode || anyRecipe
            ? 'rich-text-list unordered-list recipe-list'
            : 'rich-text-list unordered-list'
        return `<ul class="${listClass}">${itemsHtml}</ul>`
      },
      'ordered-list': (node: any, next: any) => {
        const content = next(node.content)
        // Check if this might be a recipe list based on content
        const isRecipeList =
          (cfg?.listVariant || 'default') === 'recipe' ||
          content.toLowerCase().includes('ingrediens') ||
          content.toLowerCase().includes('recept') ||
          content.toLowerCase().includes('steg')
        const listClass = isRecipeList
          ? 'rich-text-list ordered-list recipe-list'
          : 'rich-text-list ordered-list'
        return `<ol class="${listClass}">${content}</ol>`
      },
      'list-item': (node: any, next: any) => {
        // For cases outside unordered-list handler (e.g., ordered lists), keep default li
        const content = next(node.content)
        return `<li class="rich-text-list-item">${content}</li>`
      },
    },
    renderMark: {
      bold: (text: string) => {
        // Don't render bold tags for empty text
        if (!text || text.trim() === '') {
          return text
        }
        return `<strong>${text}</strong>`
      },
    },
  }

  return documentToHtmlString(document, rendererOptions)
}
